<template>
  <div v-if="show" class="fixed inset-0 z-[70] overflow-hidden">
    <!-- Background overlay -->
    <div class="absolute inset-0 bg-black bg-opacity-75" @click="handleBackdropClick"></div>
    
    <!-- Modal content with adaptive width -->
    <div class="relative w-full h-full flex items-center justify-start p-4 transition-all duration-300"
         :class="dualMode ? 'justify-start' : 'justify-center'">
      <div class="relative h-full bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col transition-all duration-300"
           :class="dualMode ? 'w-[58%] mr-2' : 'w-full max-w-7xl'">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 border-b border-blue-800">
          <div class="flex items-center space-x-4">
            <h3 class="text-xl font-semibold text-white">
              {{ $t('patientDetails.videoRoom') || 'Видеокомната' }}
            </h3>
            <!-- Video active indicator -->
            <div v-if="isVideoActive" class="flex items-center space-x-2 px-3 py-1 bg-white bg-opacity-20 rounded-full">
              <div class="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span class="text-white text-sm font-medium">В эфире</span>
            </div>
          </div>
          
          <div class="flex items-center space-x-2">
            <!-- Invite to consilium button - show when not in dual mode -->
            <button 
              v-if="!dualMode && (appointment?.status === 'scheduled' || appointment?.status === 'in_progress')"
              @click="openConsilium"
              class="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 shadow-lg"
              :title="$t('consilium.inviteTooltip') || 'Пригласить другого специалиста'"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <span class="font-medium">{{ $t('consilium.invite') }}</span>
            </button>
            
            <!-- Conclusion button - always show when not in dual mode -->
            <button 
              v-if="!dualMode"
              @click="openConclusion"
              class="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors duration-200 shadow-lg"
              :title="$t('medical.conclusion.title') || 'Заключение по приему'"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <span class="font-medium">Заключение</span>
            </button>
            
            <!-- Close button -->
            <button 
              @click="closeModal"
              class="text-white hover:bg-white hover:bg-opacity-20 text-2xl font-bold w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200"
              :title="$t('common.close') || 'Закрыть'"
            >
              ✕
            </button>
          </div>
        </div>
        
        <!-- iframe container -->
        <div class="flex-1 relative bg-gray-900">
          <iframe 
            v-if="roomUrl"
            :src="roomUrl" 
            class="absolute inset-0 w-full h-full border-0"
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            allowfullscreen
            @load="handleIframeLoad"
          ></iframe>
          <div v-else class="absolute inset-0 flex items-center justify-center">
            <div class="text-center text-white">
              <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
              <p class="text-gray-300">{{ $t('patientDetails.videoRoomUnavailable') || 'URL видеокомнаты недоступен' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoRoomModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    roomUrl: {
      type: String,
      default: ''
    },
    appointment: {
      type: Object,
      default: null
    },
    dualMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isVideoActive: true // Assume active when modal is open
    }
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },
    handleBackdropClick() {
      if (!this.dualMode) {
        this.closeModal()
      }
    },
    openConclusion() {
      this.$emit('open-conclusion')
    },
    openConsilium() {
      this.$emit('open-consilium', this.appointment)
    },
    handleIframeLoad() {
      this.isVideoActive = true
      this.$emit('video-active-changed', true)
    }
  },
  watch: {
    show(newVal) {
      // Prevent body scroll when modal is open
      if (newVal) {
        document.body.style.overflow = 'hidden'
        this.isVideoActive = true
      } else {
        document.body.style.overflow = ''
        this.isVideoActive = false
        this.$emit('video-active-changed', false)
      }
    }
  },
  beforeUnmount() {
    // Clean up body overflow style
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
/* Additional styles if needed */
</style>
